import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, Select, Store, ofActionSuccessful } from '@ngxs/store';
import { AcademicRegistrationData, AcademicState, FlowStarted, SetAcademicRegistration, SetAcademicUserType, UserType } from '@platform-frontend/checkout';
import { LicenseScope } from '@platform-frontend/marketplace';
import { CustomValidators } from '@platform-frontend/ui-tools';
import { Navigate } from '@platform-frontend/util';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

interface AcademicRegistrationForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  organizationName: FormControl<string>;
  organizationWeb: FormControl<string>;
}

interface UserTypeAndAcademicRegistrationForm {
  userType: FormControl<UserType>;
  academicRegistration: FormGroup<AcademicRegistrationForm>;
}

@UntilDestroy()
@Component({
  selector: 'yat-academic-registration',
  templateUrl: './academic-registration.component.html',
  styleUrls: ['./academic-registration.component.scss']
})
export class AcademicRegistrationComponent implements OnInit {
  constructor(private actions$: Actions, private activatedRoute: ActivatedRoute, private store: Store) {}

  readonly userTypeAndAcademicRegistrationForm = new FormGroup<UserTypeAndAcademicRegistrationForm>({
    userType: new FormControl('STUDENT', { nonNullable: true, validators: Validators.required }),
    academicRegistration: new FormGroup<AcademicRegistrationForm>({
      firstName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      lastName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
      organizationName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      organizationWeb: new FormControl('', { nonNullable: true, validators: [Validators.required, CustomValidators.looseUrl] })
    })
  });

  academicRegistrationForm: FormGroup<AcademicRegistrationForm>;

  @Select(AcademicState.academicRegistration)
  academicRegistration$: Observable<AcademicRegistrationData | null>;

  @Select(AcademicState.userType)
  userType$: Observable<UserType | null>;

  @Select(AcademicState.licenseScope)
  licenseScope$: Observable<LicenseScope | null>;

  ngOnInit(): void {
    this.academicRegistrationForm = this.userTypeAndAcademicRegistrationForm.get('academicRegistration') as FormGroup<AcademicRegistrationForm>;

    this.initData()
      .pipe(
        switchMap(() => this.actions$),
        ofActionSuccessful(FlowStarted),
        take(1),
        switchMap(() => this.initData()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private initData(): Observable<unknown> {
    const sanitizedUserType$: Observable<UserType> = this.userType$.pipe(map(userType => userType ?? 'STUDENT'));

    const sanitizedAcademicRegistration$: Observable<Omit<AcademicRegistrationData, 'licenseScope'>> = this.academicRegistration$.pipe(
      map(
        academicRegistration =>
          academicRegistration ?? {
            firstName: this.academicRegistrationForm.value.firstName ?? '',
            lastName: this.academicRegistrationForm.value.lastName ?? '',
            email: this.academicRegistrationForm.value.email ?? '',
            organizationName: this.academicRegistrationForm.value.organizationName ?? '',
            organizationWeb: this.academicRegistrationForm.value.organizationWeb ?? ''
          }
      )
    );

    return combineLatest([sanitizedUserType$, sanitizedAcademicRegistration$]).pipe(
      take(1),
      tap(([userType, academicRegistration]) => this.userTypeAndAcademicRegistrationForm.patchValue({ userType, academicRegistration }))
    );
  }

  selectUserType(userType: UserType): void {
    this.userTypeAndAcademicRegistrationForm.patchValue({ userType });
  }

  submitForm(value: { userType: UserType; academicRegistration: Omit<AcademicRegistrationData, 'licenseScope'> }): void {
    const userType = value.userType;
    const academicRegistration = value.academicRegistration;
    const followUpUrl = this.getFollowUpUrl(userType);

    this.store
      .dispatch(new SetAcademicUserType(userType))
      .pipe(
        switchMap(() => this.getLicenseScope(userType)),
        take(1),
        switchMap(licenseScope => this.store.dispatch(new SetAcademicRegistration({ ...academicRegistration, licenseScope }))),
        switchMap(() => this.store.dispatch(new Navigate([followUpUrl], { relativeTo: this.activatedRoute }))),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private getLicenseScope(userType: UserType): Observable<LicenseScope> {
    switch (userType) {
      case 'STUDENT':
        return of('PERSONAL_USE');
      case 'TEACHER':
        return this.licenseScope$.pipe(map(licenseScope => licenseScope ?? 'PERSONAL_USE'));
      case 'ORGANIZATION':
        return of('CONCURRENT_USE');
    }
  }

  private getFollowUpUrl(userType: UserType): string {
    switch (userType) {
      case 'STUDENT':
        return '../student-details';
      case 'TEACHER':
        return '../teacher-toggle';
      case 'ORGANIZATION':
        return '../verify-employee';
    }
  }
}
